// DesktopDevChatChatsPicker - Chats button + popup with search and list
// Follows DesktopDevChatModelPicker pattern: button, popup panel, click-outside

import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { RevoltButton, RevoltIcon } from '../../../../../components';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import './DesktopDevChatChatsPicker.css';

const DEFAULT_CHAT_NAME = 'Unnamed Chat';
const VIEWPORT_MARGIN = 20;

function getChatDisplayName(chat) {
  return (chat && chat.name) || DEFAULT_CHAT_NAME;
}

function getModifiedAt(chat) {
  return chat?.modifiedAt || chat?.updatedAt || chat?.createdAt || '';
}

export function DesktopDevChatChatsPicker({
  chats = [],
  activeChatId,
  onSelectChat,
  onCreateChat,
  onDeleteChat,
  disabled = false,
}) {
  const [popupOpen, setPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const popupRef = useRef(null);

  const filteredChats = useCallback(() => {
    if (!searchQuery.trim()) return chats;
    const q = searchQuery.toLowerCase();
    return chats.filter((c) => {
      const name = getChatDisplayName(c).toLowerCase();
      return name.includes(q);
    });
  }, [chats, searchQuery]);

  const filtered = filteredChats();

  const handleSelectChat = (chat) => {
    onSelectChat?.(chat.id);
    setPopupOpen(false);
  };

  const handleCreateChat = () => {
    onCreateChat?.();
    setPopupOpen(false);
  };

  const handleDeleteChat = (e, chatId) => {
    e.stopPropagation();
    onDeleteChat?.(chatId);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPopupOpen(false);
      }
    };
    if (popupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [popupOpen]);

  useLayoutEffect(() => {
    if (!popupOpen || !containerRef.current || !popupRef.current) return;
    const btnRect = containerRef.current.getBoundingClientRect();
    const popupEl = popupRef.current;
    const popupWidth = popupEl.offsetWidth;
    const popupHeight = popupEl.offsetHeight;
    const gap = 6;
    const margin = VIEWPORT_MARGIN;

    let top = btnRect.bottom + gap;
    let left = btnRect.right - popupWidth;

    if (left < margin) left = margin;
    if (left + popupWidth > window.innerWidth - margin) left = window.innerWidth - popupWidth - margin;
    if (top < margin) top = margin;
    if (top + popupHeight > window.innerHeight - margin) {
      top = btnRect.top - popupHeight - gap;
      if (top < margin) top = margin;
    }

    popupEl.style.top = `${top}px`;
    popupEl.style.left = `${left}px`;
  }, [popupOpen]);

  return (
    <div className="desktop-dev-chat-chats-picker" ref={containerRef}>
      <RevoltButton
        variant="outline"
        size="default"
        onClick={() => setPopupOpen((prev) => !prev)}
        disabled={disabled}
        className="desktop-dev-chat-chats-picker__btn"
        title="Chats"
        aria-expanded={popupOpen}
        aria-haspopup="true"
      >
        <RevoltIcon name="lucide:message-square" size="sm" />
        <span className="desktop-dev-chat-chats-picker__btn-label">Chats</span>
      </RevoltButton>

      {popupOpen && (
        <div
          ref={popupRef}
          className="desktop-dev-chat-chats-picker__popup"
        >
          <div className="desktop-dev-chat-chats-picker__search-wrap">
            <i className="fa-solid fa-magnifying-glass desktop-dev-chat-chats-picker__search-icon" aria-hidden />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="desktop-dev-chat-chats-picker__search-input"
              autoFocus
            />
          </div>
          <RevoltButton
            variant="primary"
            size="sm"
            onClick={handleCreateChat}
            className="desktop-dev-chat-chats-picker__new-btn"
          >
            <RevoltIcon name="plus" size="sm" />
            <span>New chat</span>
          </RevoltButton>
          <ul className="desktop-dev-chat-chats-picker__list">
            {filtered.length === 0 ? (
              <li className="desktop-dev-chat-chats-picker__empty">
                {chats.length === 0 ? 'No chats yet' : 'No chats match'}
              </li>
            ) : (
              filtered.map((chat) => (
                <li key={chat.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`desktop-dev-chat-chats-picker__item ${
                      chat.id === activeChatId ? 'desktop-dev-chat-chats-picker__item--active' : ''
                    }`}
                    onClick={() => handleSelectChat(chat)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectChat(chat);
                      }
                    }}
                  >
                    <span className="desktop-dev-chat-chats-picker__item-text">
                      <span className="desktop-dev-chat-chats-picker__item-name">{getChatDisplayName(chat)}</span>
                      <span className="desktop-dev-chat-chats-picker__item-meta">
                        Modified {formatRelativeTime(getModifiedAt(chat))}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="desktop-dev-chat-chats-picker__item-delete"
                      onClick={(e) => handleDeleteChat(e, chat.id)}
                      title="Delete chat"
                      aria-label="Delete chat"
                    >
                      <RevoltIcon name="lucide:trash2" size="sm" />
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
